import moment from 'moment';
import { useEffect, useState } from 'react';
import Datetime from 'react-datetime';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody } from 'reactstrap';

import crossBlack from '../../../assets/img/icons/cross_black.svg';
import plusIcon from '../../../assets/img/icons/plus_white.svg';
import CreateProjectDropdown from '../../../components/DropdownStyle/CreateProjectDropdown';
import ButtonTextLoader from '../../../components/Loaders/ButtonTextLoader';
import Select from '../../../components/ReactSelectDropdown';
import { filterOption } from '../../../components/ReactSelectDropdown/ReactSelectDropdown';
import { ClearSearchButton } from '../../../components/Search/searchStyles';
import TimePicker from '../../../components/TimePicker';
import useAutocompleteService from '../../../customHooks/usePlacesAutocompleteService';
import {
  ButtonIcon,
  ColoredButton,
  ColoredButtonWithIcon,
  PrimaryButton,
  RadiusButton,
  WhiteButton,
  WhiteButtonWithIcon,
} from '../../../styledComponents/buttons';
import {
  ColoredText,
  CommonFlex,
  CommonGrid,
  CommonImage,
  CommonText,
  FormAlert,
  InputField,
  InputLabel,
  InputWithLabelSection,
  RedStar,
} from '../../../styledComponents/common';
import { checkHtml, checkUrl } from '../../../utils/helper';
import { InputSection } from '../CreateTask/createTaskStyles';

const AddNewTaskModal = ({
  isOpen,
  toggle,
  statusOptions,
  periodList,
  createMasterTask,
  updateMasterTask,
  archiveMasterTask,
  isCreateMasterTaskLoading,
  isUpdateMasterTaskLoading,
  isArchiveMasterTaskLoading,
  createdMasterTaskInfo,
  updatedMasterTaskInfo,
  clearMasterTaskState,
  onTaskCreated,
  //auto selecting value
  prefillTaskType,
  prefillTaskName = '',
  prefillNewTaskId = null,
  onNewTaskUsed,
  isEditMode = false,
  taskToEdit = null,
}) => {
  const { t } = useTranslation();
  const [selectedTaskType, setSelectedTaskType] = useState('');
  const [selectedPeriodType, setSelectedPeriodType] = useState('');
  const [taskName, setTaskName] = useState('');
  const [saveErrors, setSaveErrors] = useState({});
  const [time, setTime] = useState(moment('00:00', 'HH:mm')); // Default time
  const [linkedNewTaskId, setLinkedNewTaskId] = useState(null);
  const [originalTaskName, setOriginalTaskName] = useState('');

  // useEffect(() => {
  //   if (createdMasterTaskInfo) {
  //     handleToggle(); // close modal
  //   }
  // }, [createdMasterTaskInfo]);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && taskToEdit) {
        // In edit mode, populate from taskToEdit
        const taskData = taskToEdit.taskData;

        // Find and set task type
        const taskTypeOption = statusOptions.find(opt => opt.value === taskData.task_type);
        setSelectedTaskType(taskTypeOption || '');

        // Set task name and store original for comparison
        const name = taskData.name || '';
        setTaskName(name);
        setOriginalTaskName(name);

        // Set duration - fix duration calculation
        const duration = taskData.default_task_duration || "1.00";
        const durationValue = parseFloat(duration);
        const durationHours = Math.floor(durationValue);
        const durationMinutes = Math.round((durationValue - durationHours) * 60);

        // Format time string properly
        const timeString = `${durationHours.toString().padStart(2, '0')}:${durationMinutes.toString().padStart(2, '0')}`;
        console.log('timeString', timeString);
        // setTime(moment(timeString, 'HH:mm'));
        setTime(moment(timeString, 'HH:mm').format('HH:mm'));

        // Find and set period
        const periodOption = periodList.find(opt => opt.value === taskData.task_period);
        setSelectedPeriodType(periodOption || '');
      } else {
        // In create mode, use prefill values
        setSelectedTaskType(prefillTaskType || '');
        setTaskName(prefillTaskName || '');
        setOriginalTaskName('');
        setLinkedNewTaskId(prefillNewTaskId);
        setSelectedPeriodType('');
        setTime(moment('00:00', 'HH:mm').format('HH:mm'));
      }
      setSaveErrors({});
    }
  }, [isOpen, isEditMode, taskToEdit, statusOptions, periodList]);

  useEffect(() => {
    if (createdMasterTaskInfo) {
      if (typeof onTaskCreated === 'function') {
        onTaskCreated(createdMasterTaskInfo); // ✅ inform parent first
      }
      if (linkedNewTaskId && typeof onNewTaskUsed === 'function') {
        onNewTaskUsed(linkedNewTaskId); // ✅ remove from right table
      }
      handleToggle(); // then close and clear
    }
  }, [createdMasterTaskInfo]);

  useEffect(() => {
    if (updatedMasterTaskInfo) {
      // Modal will be closed by parent component
    }
  }, [updatedMasterTaskInfo]);

  const handleTimeChange = value => {
    const start_time = value.replace(/ /g, '');
    setTime(moment(start_time, 'h:mmA').format('HH:mm'));
  };

  const onTaskTypeChange = e => {
    setSelectedTaskType(e);
  };

  const onPeriodTypeChange = e => {
    setSelectedPeriodType(e);
  };

  const onTaskNameChange = e => {
    let errors = { ...saveErrors };
    delete errors['taskName'];
    setSaveErrors(errors);
    setTaskName(e.target.value);
  };

  const checkError = () => {
    let saveErrors = {};
    let formIsValid = true;

    if (!selectedTaskType?.value) {
      formIsValid = false;
      saveErrors['taskType'] = t('task_type_is_required');
    }

    setSaveErrors(saveErrors);
    return formIsValid;
  };

  const clearState = () => {
    setSelectedTaskType('');
    setSelectedPeriodType('');
    setTaskName('');
    setTime(moment('00:00', 'HH:mm'));
    setSaveErrors({});
    clearMasterTaskState();
  };

  const handleToggle = () => {
    clearState();
    toggle();
  };

  const handleMasterTaskCreate = () => {
    const parsedTime = moment(time, 'HH:mm');
    const hours = parsedTime.hours();
    const minutes = parsedTime.minutes();
    const durationInHours = hours + minutes / 60;

    const payload = {
      name: taskName.trim(),
      task_type: selectedTaskType.value,
      default_task_duration: parseFloat(durationInHours.toFixed(2)), // as number, not string
      task_period: selectedPeriodType.value,
      ...(linkedNewTaskId && { new_task_id: linkedNewTaskId }), //  conditionally include
    };

    if (isEditMode && taskToEdit) {
      // Call update function with all editable fields
      updateMasterTask({
        id: taskToEdit.taskData.id,
        name: taskName.trim(),
        default_task_duration: parseFloat(durationInHours.toFixed(2)),
        task_period: selectedPeriodType.value
      });
    } else {
      // Create new master task
      createMasterTask(payload);
    }
  };

  const handleArchiveTask = () => {
    console.log('taskToEdit', taskToEdit);
    if (isEditMode && taskToEdit) {
      // Call archive function directly with properly formatted object
      archiveMasterTask({
        id: taskToEdit.taskData.id
      });
    }
  };

  // Add a helper function to check if the form is valid in edit mode
  const isEditFormValid = () => {
    // Check if any field has been changed
    const parsedTime = moment(time, 'HH:mm');
    const hours = parsedTime.hours();
    const minutes = parsedTime.minutes();
    const currentDurationInHours = parseFloat((hours + minutes / 60).toFixed(2));

    const originalDuration = parseFloat(taskToEdit?.taskData?.default_task_duration || 0);
    const originalPeriod = taskToEdit?.taskData?.task_period;

    const nameChanged = taskName.trim() !== originalTaskName.trim();
    const durationChanged = currentDurationInHours !== originalDuration;
    const periodChanged = selectedPeriodType?.value !== originalPeriod;

    return (nameChanged || durationChanged || periodChanged) &&
           taskName.trim() &&
           selectedPeriodType?.value;
  };

  const timeMoment = moment(time, 'HH:mm', true); // strict format

  const isFormValid = isEditMode
    ? isEditFormValid()
    : (
      taskName.trim() &&
      selectedTaskType?.value &&
      selectedPeriodType?.value &&
      timeMoment.isValid() &&
      (timeMoment.hours() !== 0 || timeMoment.minutes() !== 0)
    );

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      style={{
        maxWidth: '1050px',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -65%)',
        margin: 0,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '15px',
          right: '15px',
          cursor: 'pointer',
          zIndex: 1000,
        }}
      >
        <img
          src={crossBlack}
          alt="Close"
          onClick={handleToggle}
          style={{ width: '25px', height: '25px' }}
        />
      </div>
      <ModalBody style={{ padding: `0`, margin: `30px 30px` }}>
        <CommonText style={{ fontSize: '24px', marginBottom: '20px', fontWeight: 'bold' }}>
          {isEditMode ? 'Edit Master Task' : 'Add New Master Task'}
        </CommonText>
        <InputSection>
          <InputWithLabelSection>
            <InputLabel style={{ marginBottom: '10px' }}>
              {t('task_type')} <RedStar>*</RedStar>
            </InputLabel>
            <Select
              isSearchable
              value={selectedTaskType}
              options={statusOptions}
              placeholder={t('select_type')}
              onChange={e => {
                onTaskTypeChange(e);
              }}
              styles={CreateProjectDropdown()}
              isDisabled={isEditMode}
            />{' '}
            {saveErrors &&
              saveErrors['taskType'] &&
              saveErrors['taskType'] !== undefined && (
                <FormAlert>{saveErrors['taskType']}</FormAlert>
              )}
          </InputWithLabelSection>
          <InputWithLabelSection
            style={{ width: '150px', position: 'relative' }}
          >
            <InputLabel style={{ marginBottom: '10px' }}>
              Estimated Duration <RedStar>*</RedStar>
            </InputLabel>
            <TimePicker
              value={time}
              onChange={value => handleTimeChange(value)}
            />
            <span
              style={{
                position: 'absolute',
                right: '-35px',
                top: '40px',
                pointerEvents: 'none',
                color: '#666',
              }}
            >
              Hrs
            </span>
          </InputWithLabelSection>
        </InputSection>
        {isEditMode ? (
          <>
            <CommonFlex style={{ marginTop: '20px', gap: '20px' }}>
              <InputWithLabelSection style={{ flex: 1 }}>
                <InputLabel style={{ marginBottom: '10px' }}>
                  Period <RedStar>*</RedStar>
                </InputLabel>
                <Select
                  isSearchable
                  value={selectedPeriodType}
                  options={periodList}
                  placeholder={t('select_period')}
                  onChange={e => {
                    onPeriodTypeChange(e);
                  }}
                  styles={CreateProjectDropdown()}
                />
              </InputWithLabelSection>
              <InputWithLabelSection style={{ flex: 1 }}>
                <InputLabel style={{ marginBottom: '10px' }}>
                  {t('task_name')} <RedStar>*</RedStar>
                </InputLabel>
                <InputField
                  type="text"
                  value={taskName}
                  onChange={onTaskNameChange}
                  placeholder={t('enter_task_name')}
                />
              </InputWithLabelSection>
            </CommonFlex>
          </>
        ) : (
          <CommonFlex style={{ marginTop: '20px', gap: '20px' }}>
            <InputWithLabelSection style={{ flex: 1 }}>
              <InputLabel style={{ marginBottom: '10px' }}>
                Period <RedStar>*</RedStar>
              </InputLabel>
              <Select
                isSearchable
                value={selectedPeriodType}
                options={periodList}
                placeholder={t('select_period')}
                onChange={e => {
                  onPeriodTypeChange(e);
                }}
                styles={CreateProjectDropdown()}
              />
            </InputWithLabelSection>
            <InputWithLabelSection style={{ flex: 1 }}>
              <InputLabel style={{ marginBottom: '10px' }}>
                {t('task_name')} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                value={taskName}
                onChange={onTaskNameChange}
                placeholder={t('enter_task_name')}
              />
            </InputWithLabelSection>
          </CommonFlex>
        )}
        <CommonFlex style={{ marginTop: '20px', gap: '10px', justifyContent: 'flex-end' }}>
          {isEditMode && (
            <WhiteButtonWithIcon
              onClick={handleArchiveTask}
              style={{ marginRight: 'auto' }}
              disabled={isArchiveMasterTaskLoading}
            >
              {isArchiveMasterTaskLoading ? (
                <ButtonTextLoader color="#000" loadingText="Archiving" size={4} fontSize="12px" />
              ) : (
                'Archive Task'
              )}
            </WhiteButtonWithIcon>
          )}
          <WhiteButton onClick={handleToggle}>Cancel</WhiteButton>
          <PrimaryButton
            onClick={handleMasterTaskCreate}
            disabled={
              !isFormValid ||
              isCreateMasterTaskLoading ||
              isUpdateMasterTaskLoading
            }
          >
            {isCreateMasterTaskLoading || isUpdateMasterTaskLoading ? (
              <ButtonTextLoader
                color="#fff"
                loadingText={isEditMode ? 'Updating' : 'Creating'}
                size={4}
                fontSize="12px"
              />
            ) : isEditMode ? (
              'Update Task'
            ) : (
              'Create Task'
            )}
          </PrimaryButton>
        </CommonFlex>
      </ModalBody>
    </Modal>
  );
};

export default AddNewTaskModal;
